import { RadioGroup as HeadlessRadioGroup, RadioField, Radio, RadioLabel } from './fragments/Radio';
import { Label } from './fragments/FieldSet';
import { useCallback, useMemo } from 'react';

export interface RadioOption<T = string | boolean> {
  value: T;
  label: string;
}

interface RadioGroupProps<T = string | boolean> {
  value: T | undefined;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  disabled?: boolean;
  label?: string;
  required?: boolean;
  name: string;
  direction?: 'horizontal' | 'vertical';
}

// Convertit une valeur en string pour HeadlessUI
const valueToString = <T,>(val: T | undefined): string => {
  if (val === undefined) return '';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return String(val);
};

// Convertit un string en valeur originale
const stringToValue = <T,>(str: string, options: RadioOption<T>[]): T | undefined => {
  const option = options.find(opt => valueToString(opt.value) === str);
  return option?.value;
};

export const RadioGroupComponent = <T extends string | boolean = string | boolean>({
  value,
  onChange,
  options,
  disabled,
  label,
  required,
  name,
  direction = 'horizontal'
}: RadioGroupProps<T>) => {
  const stringValue = useMemo(() => valueToString(value), [value]);

  const handleChange = useCallback((strValue: string) => {
    const originalValue = stringToValue(strValue, options);
    if (originalValue !== undefined) {
      onChange(originalValue);
    }
  }, [onChange, options]);

  return (
    <div className="flex flex-col gap-2">
      {label && <Label required={required}>{label}</Label>}
      <HeadlessRadioGroup
        value={stringValue}
        onChange={handleChange}
        disabled={disabled}
        name={name}
        className={direction === 'vertical' ? 'flex-col' : 'flex-row'}
      >
        {options.map((option) => (
          <RadioField key={valueToString(option.value)}>
            <Radio value={valueToString(option.value)} />
            <RadioLabel>{option.label}</RadioLabel>
          </RadioField>
        ))}
      </HeadlessRadioGroup>
    </div>
  );
};
